import type {
  SmartTableChairModel,
  SmartTableConfig,
} from "../../../../types/floorplannerTypes";
import {
  DINING_ARMCHAIR_SIZE_M,
  DINING_CHAIR_DEPTH_M,
  DINING_CHAIR_TABLE_GAP_M,
  DINING_CHAIR_WIDTH_M,
} from "./diningLayoutConstants";
import { normalizeSmartTableConfig } from "./smartTableConfig";

export const SMART_TABLE_TOP_THICKNESS_M = 0.06;
export const SMART_TABLE_LEG_SIZE_M = 0.045;
export const SMART_TABLE_RECT_LEG_INSET_M = 0.12;
export const SMART_TABLE_RECT_MAX_LEG_SPAN_M = 1.8;
export const SMART_TABLE_PEDESTAL_COLUMN_DIAMETER_M = 0.18;
export const SMART_TABLE_PEDESTAL_BASE_DIAMETER_M = 0.45;

export type SmartTableEdge = "left" | "right" | "start" | "end" | "round";

export type SmartTableChairPlacement = {
  id: string;
  edge: SmartTableEdge;
  index: number;
  x: number;
  y: number;
  /** Direction from table centre toward the back of the chair. */
  outwardAngle: number;
  width: number;
  depth: number;
  height: number;
  model: SmartTableChairModel;
};

export type SmartTableLegPlacement = {
  id: string;
  kind: "leg";
  x: number;
  y: number;
  size: number;
};

export type SmartTablePedestalPlacement = {
  id: string;
  kind: "pedestal";
  x: number;
  y: number;
  columnDiameter: number;
  baseDiameter: number;
};

export type SmartTableSupportPlacement =
  | SmartTableLegPlacement
  | SmartTablePedestalPlacement;

export type SmartTableBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

export type ResolvedSmartTableLayout = {
  config: SmartTableConfig;
  tabletop: {
    shape: SmartTableConfig["shape"];
    width: number;
    length: number;
  };
  chairs: SmartTableChairPlacement[];
  supports: SmartTableSupportPlacement[];
  chairCounts: {
    left: number;
    right: number;
    start: number;
    end: number;
    round: number;
    total: number;
  };
  bounds: SmartTableBounds;
  legCount: number;
};

export function getSmartTableChairDimensions(model: SmartTableChairModel): {
  width: number;
  depth: number;
  height: number;
} {
  return model === "armchair"
    ? {
        width: DINING_ARMCHAIR_SIZE_M,
        depth: DINING_ARMCHAIR_SIZE_M,
        height: 0.76,
      }
    : {
        width: DINING_CHAIR_WIDTH_M,
        depth: DINING_CHAIR_DEPTH_M,
        height: 0.82,
      };
}

export type SmartTableArmchairProfile = {
  seat: { width: number; depth: number; centerY: number };
  arm: {
    width: number;
    depth: number;
    centerX: number;
    centerY: number;
  };
  back: { width: number; depth: number; centerY: number };
};

/**
 * Shared visible armchair profile for smart tables. The seat reaches the front
 * edge, the back reaches the rear edge, and the arms reach both side edges so
 * the rendered 2D and 3D model fills its declared footprint exactly.
 */
export function resolveSmartTableArmchairProfile(
  width: number,
  depth: number,
): SmartTableArmchairProfile {
  const armWidth = Math.min(width * 0.11, 0.05);
  const backDepth = Math.min(depth * 0.17, 0.075);

  return {
    seat: {
      width: width * 0.82,
      depth: depth - backDepth,
      centerY: -backDepth / 2,
    },
    arm: {
      width: armWidth,
      depth: depth * 0.72,
      centerX: width / 2 - armWidth / 2,
      centerY: -depth * 0.04,
    },
    back: {
      width: width * 0.9,
      depth: backDepth,
      centerY: depth / 2 - backDepth / 2,
    },
  };
}

const distributedCenters = (count: number, edgeLength: number): number[] => {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const margin = edgeLength / (2 * count);
  const start = -edgeLength / 2 + margin;
  const step = (edgeLength - 2 * margin) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + index * step);
};

function rectangleChairCounts(config: Extract<SmartTableConfig, { shape: "rectangle" }>) {
  if (!config.chairs.enabled) {
    return { left: 0, right: 0, start: 0, end: 0 };
  }
  if (config.chairs.mode === "manual") {
    return { ...config.chairs.manualCounts };
  }
  const sideCount = Math.max(
    1,
    Math.floor((config.length + 1e-9) / config.chairs.seatSpacing),
  );
  return {
    left: sideCount,
    right: sideCount,
    start: config.chairs.automaticEnds.start ? 1 : 0,
    end: config.chairs.automaticEnds.end ? 1 : 0,
  };
}

function createRectangleChairs(
  config: Extract<SmartTableConfig, { shape: "rectangle" }>,
): { chairs: SmartTableChairPlacement[]; counts: ReturnType<typeof rectangleChairCounts> } {
  const counts = rectangleChairCounts(config);
  const dimensions = getSmartTableChairDimensions(config.chairs.model);
  const sideOffset =
    config.width / 2 + DINING_CHAIR_TABLE_GAP_M + dimensions.depth / 2;
  const endOffset =
    config.length / 2 + DINING_CHAIR_TABLE_GAP_M + dimensions.depth / 2;
  const chairs: SmartTableChairPlacement[] = [];

  distributedCenters(counts.left, config.length).forEach((y, index) => {
    chairs.push({
      id: `left-${index}`,
      edge: "left",
      index,
      x: -sideOffset,
      y,
      outwardAngle: Math.PI,
      ...dimensions,
      model: config.chairs.model,
    });
  });
  distributedCenters(counts.right, config.length).forEach((y, index) => {
    chairs.push({
      id: `right-${index}`,
      edge: "right",
      index,
      x: sideOffset,
      y,
      outwardAngle: 0,
      ...dimensions,
      model: config.chairs.model,
    });
  });
  distributedCenters(counts.start, config.width).forEach((x, index) => {
    chairs.push({
      id: `start-${index}`,
      edge: "start",
      index,
      x,
      y: -endOffset,
      outwardAngle: -Math.PI / 2,
      ...dimensions,
      model: config.chairs.model,
    });
  });
  distributedCenters(counts.end, config.width).forEach((x, index) => {
    chairs.push({
      id: `end-${index}`,
      edge: "end",
      index,
      x,
      y: endOffset,
      outwardAngle: Math.PI / 2,
      ...dimensions,
      model: config.chairs.model,
    });
  });

  return { chairs, counts };
}

function createRoundChairs(
  config: Extract<SmartTableConfig, { shape: "round" }>,
): SmartTableChairPlacement[] {
  if (!config.chairs.enabled) return [];
  const count =
    config.chairs.mode === "manual"
      ? config.chairs.manualCount
      : Math.max(
          1,
          Math.floor(
            (Math.PI * config.diameter + 1e-9) / config.chairs.seatSpacing,
          ),
        );
  const dimensions = getSmartTableChairDimensions(config.chairs.model);
  const orbit =
    config.diameter / 2 + DINING_CHAIR_TABLE_GAP_M + dimensions.depth / 2;

  return Array.from({ length: count }, (_, index) => {
    const angle = (Math.PI * 2 * index) / count;
    return {
      id: `round-${index}`,
      edge: "round" as const,
      index,
      x: Math.cos(angle) * orbit,
      y: Math.sin(angle) * orbit,
      outwardAngle: angle,
      ...dimensions,
      model: config.chairs.model,
    };
  });
}

function createRectangleSupports(
  width: number,
  length: number,
): SmartTableLegPlacement[] {
  const usableLength = Math.max(length - 2 * SMART_TABLE_RECT_LEG_INSET_M, 0);
  const stationCount = Math.max(
    2,
    Math.ceil(usableLength / SMART_TABLE_RECT_MAX_LEG_SPAN_M) + 1,
  );
  const xOffset = Math.max(width / 2 - SMART_TABLE_RECT_LEG_INSET_M, 0);
  const yStart = -length / 2 + SMART_TABLE_RECT_LEG_INSET_M;
  const yEnd = length / 2 - SMART_TABLE_RECT_LEG_INSET_M;
  const yStep = stationCount > 1 ? (yEnd - yStart) / (stationCount - 1) : 0;

  return Array.from({ length: stationCount }).flatMap((_, station) => {
    const y = yStart + yStep * station;
    return [-1, 1].map((side) => ({
      id: `rect-leg-${station}-${side < 0 ? "left" : "right"}`,
      kind: "leg" as const,
      x: side * xOffset,
      y,
      size: SMART_TABLE_LEG_SIZE_M,
    }));
  });
}

function createRoundSupports(
  config: Extract<SmartTableConfig, { shape: "round" }>,
): SmartTableSupportPlacement[] {
  if (config.support === "pedestal") {
    return [
      {
        id: "round-pedestal",
        kind: "pedestal",
        x: 0,
        y: 0,
        columnDiameter: SMART_TABLE_PEDESTAL_COLUMN_DIAMETER_M,
        baseDiameter: Math.min(
          SMART_TABLE_PEDESTAL_BASE_DIAMETER_M,
          Math.max(config.diameter - 0.1, SMART_TABLE_PEDESTAL_COLUMN_DIAMETER_M),
        ),
      },
    ];
  }
  const orbit = config.diameter * 0.3;
  return Array.from({ length: 4 }, (_, index) => {
    const angle = Math.PI / 4 + (index * Math.PI) / 2;
    return {
      id: `round-leg-${index}`,
      kind: "leg" as const,
      x: Math.cos(angle) * orbit,
      y: Math.sin(angle) * orbit,
      size: SMART_TABLE_LEG_SIZE_M,
    };
  });
}

const rotatedChairCorners = (
  chair: SmartTableChairPlacement,
): Array<[number, number]> => {
  const halfDepth = chair.depth / 2;
  const halfWidth = chair.width / 2;
  const cos = Math.cos(chair.outwardAngle);
  const sin = Math.sin(chair.outwardAngle);
  return [
    [-halfDepth, -halfWidth],
    [halfDepth, -halfWidth],
    [halfDepth, halfWidth],
    [-halfDepth, halfWidth],
  ].map(([x, y]) => [
    chair.x + x * cos - y * sin,
    chair.y + x * sin + y * cos,
  ]);
};

function calculateBounds(
  width: number,
  length: number,
  chairs: SmartTableChairPlacement[],
): SmartTableBounds {
  const points: Array<[number, number]> = [
    [-width / 2, -length / 2],
    [width / 2, -length / 2],
    [width / 2, length / 2],
    [-width / 2, length / 2],
  ];
  chairs.forEach((chair) => points.push(...rotatedChairCorners(chair)));
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function resolveSmartTableLayout(value: unknown): ResolvedSmartTableLayout {
  const config = normalizeSmartTableConfig(value);
  if (config.shape === "round") {
    const chairs = createRoundChairs(config);
    const supports = createRoundSupports(config);
    const roundCount = chairs.length;
    return {
      config,
      tabletop: {
        shape: "round",
        width: config.diameter,
        length: config.diameter,
      },
      chairs,
      supports,
      chairCounts: {
        left: 0,
        right: 0,
        start: 0,
        end: 0,
        round: roundCount,
        total: roundCount,
      },
      bounds: calculateBounds(config.diameter, config.diameter, chairs),
      legCount: supports[0]?.kind === "pedestal" ? 1 : supports.length,
    };
  }

  const { chairs, counts } = createRectangleChairs(config);
  const supports = createRectangleSupports(config.width, config.length);
  const total = counts.left + counts.right + counts.start + counts.end;
  return {
    config,
    tabletop: {
      shape: "rectangle",
      width: config.width,
      length: config.length,
    },
    chairs,
    supports,
    chairCounts: {
      ...counts,
      round: 0,
      total,
    },
    bounds: calculateBounds(config.width, config.length, chairs),
    legCount: supports.length,
  };
}
